// TQ Connect Configuration Types

import { TQ_BADGE_COLORS } from '../../screens/TQConnect/constants';

export interface ITQUnlockPromptSettings {
  title: string;
  subtitle: string;
  ctaButtonText: string;
  ctaButtonUrl: string;
}

export interface ITQConnectConfig {
  unlockPromptSettings: ITQUnlockPromptSettings;
  label: string;
  scoreConfig: ITQScoreConfig;
  offeringName: string;
  triggers: ITQTriggers;
  badges: ITQBadges;
  allowedMangoes: string[];
  shoutoutCategories: ITQShoutoutCategories;
  ratingCategories: ITQRatingCategories;
  profileCompletionQueries: ITQProfileCompletionQuery[];
  enabled: boolean;
  setupCompleted: boolean;
}

export interface ITQScoreConfig {
  name: string;
  logo: string;
  max: number;
}

export interface ITQTriggers {
  profileCompletion: ITQTriggerWeight;
  feedbackReceived: ITQTriggerWeight;
  shoutoutReceived: ITQShoutoutTriggerWeight;
}

export interface ITQTriggerWeight {
  weight: number; // in percentage
  locked?: boolean;
}

export interface ITQShoutoutTriggerWeight extends ITQTriggerWeight {
  scoreForEach: number;
}

export interface ITQBadges extends Record<string, ITQBadge> {}

export interface ITQBadge {
  name: string;
  logo: string;
  color: string;
}

export interface ITQShoutoutCategories extends Record<
  TQShoutoutCategoryValue,
  ITQShoutoutCategory
> {}

export interface ITQShoutoutCategory {
  name: string;
  logo: string;
  label: string;
  placeholder: string;
}

export interface ITQRatingCategories extends Record<
  TQRatingCategoryValue,
  ITQRatingCategory
> {}

export interface ITQRatingCategory {
  name: string;
  logo: string;
  label: string;
  placeholder: string;
  feedback: string;
  color: string;
  score: number; // ranges from -1 to 4
}

export interface ITQProfileCompletionQuery {
  _id: string;
  question: string;
  helpText?: string;
  score: number;
  type: TQProfileQuestionType;
  enableMultipleSelection?: boolean;
  options?: string[];
  order: number;
  locked: boolean;
}

// Enums and Type Definitions
export enum TQProfileQuestionType {
  TEXT = 'text',
  MULTIPLE_CHOICE = 'multipleChoice',
  DROPDOWN = 'dropdown',
}

export type TQProfileQuestionTypeValue = `${TQProfileQuestionType}`;

export enum TQShoutoutCategory {
  MILESTONE_ACHIEVER = 'milestoneAchiever',
  TOP_CONTRIBUTOR = 'topContributor',
  CHALLENGE_CHAMP = 'challengeChamp',
  BIG_WIN = 'bigWin',
  CONSISTENCY_STAR = 'consistencyStar',
  CREATIVE_SPARK = 'creativeSpark',
  SUPPORT_HERO = 'supportHero',
  COLLABORATION_STAR = 'collaborationStar',
  MOST_HELPFUL = 'mostHelpful',
  GRATITUDE_NOTE = 'gratitudeNote',
  CUSTOM = 'custom',
}

export type TQShoutoutCategoryValue = `${TQShoutoutCategory}`;

export enum TQRatingCategory {
  POOR = 'poor',
  OKAY = 'okay',
  GOOD = 'good',
  EXCELLENT = 'excellent',
  OUTSTANDING = 'outstanding',
}

export type TQRatingCategoryValue = `${TQRatingCategory}`;

export enum TQBadgeLevel {
  LEVEL_200 = '200',
  LEVEL_500 = '500',
  LEVEL_700 = '700',
  LEVEL_900 = '900',
  LEVEL_1000 = '1000',
}

export type TQBadgeLevelValue = `${TQBadgeLevel}`;

export interface ITQConnectConfigResult {
  host: string;
  isDefaultConfig: boolean;
  tqConnectConfig: ITQConnectConfig;
}

export type TQBadgeColorType = (typeof TQ_BADGE_COLORS)[number];

// Shoutouts Types
export interface ITQShoutoutCategoryDetails {
  id: string;
  name: string;
  logo: string;
}

export interface ITQShoutoutChannel {
  _id: string;
  name: string;
}

export interface ITQShoutoutUser {
  _id: string;
  name: string;
  profilePicUrl: string;
}

export enum TQShoutoutVisibility {
  PROFILE_ONLY = 'profile-only',
  CHANNEL_ONLY = 'channel-only',
}

export type TQShoutoutVisibilityType = `${TQShoutoutVisibility}`;

export interface ITQShoutout {
  createdAt: string;
  updatedAt: string;
  _id: string;
  creator: string;
  description: string;
  category: ITQShoutoutCategoryDetails;
  coverImage: string;
  visibility: TQShoutoutVisibilityType;
  channels?: ITQShoutoutChannel[];
  from: ITQShoutoutUser;
  to: ITQShoutoutUser;
}
